import {useMemo} from 'react';
import {
  useGetIngestionSourceFolders,
  useGetIngestionSourceFoldersData,
  useGetIngestionSourceHeaders,
  useGetIngestionViewWizard,
} from '../../../../hooks/ingestions/useIngestions';
import {
  IngestionKeyValueItem,
  IlstServerLocationDetails,
} from '../../../../api/types/ingestionApi.type';

export const useIngestionDropdowns = (
  selectedServerLocation: {key: string; displayValue: string} | null,
  selectedFolder: {key: string; displayValue: string} | null,
  fieldSeparatorValue: {key: string; displayValue: string} | null,
  textQualifierValue: {key: string; displayValue: string} | null,
  dataDelimiterValue: {key: string; displayValue: string} | null,
  datLoadFile: {key: string; displayValue: string} | null,
  projectId: number
) => {
  const {data: wizardData, isLoading} = useGetIngestionViewWizard(projectId);

  const ingestionTypeOptions = useMemo(() => {
    return (
      wizardData?.overwriteOption?.map((item: any) => ({
        key: item.value,
        displayValue: item.text,
        isSelected: item.isSelected ?? false,
      })) ?? []
    );
  }, [wizardData]);
  // Server location details
  const lstServerLocation = useMemo(
    () =>
      wizardData?.lstServerLocationDetails?.map(
        (item: IlstServerLocationDetails) => ({
          value: item.value,
          text: item.text,
        })
      ) ?? [],
    [wizardData]
  );

  const rawLstServerLocation = wizardData?.lstServerLocationDetails ?? [];

  // Source System
  const sourceSystem = useMemo(
    () =>
      wizardData?.sourceSystem?.map((item: IngestionKeyValueItem) => ({
        key: item.value,
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Server Location
  const serverLocation = useMemo(
    () =>
      wizardData?.serverLocation?.map((item: IngestionKeyValueItem) => ({
        key: item.value,
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Column Delimiter
  const columnDelimeter = useMemo(
    () =>
      wizardData?.columnDelimiter?.map((item: IngestionKeyValueItem) => ({
        key: String(item.value),
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Data Delimiter
  const dataDelimeter = useMemo(
    () =>
      wizardData?.dataDelimiter?.map((item: IngestionKeyValueItem) => ({
        key: String(item.value),
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Text Qualifier
  const textQualifier = useMemo(
    () =>
      wizardData?.textQualifier?.map((item: IngestionKeyValueItem) => ({
        key: String(item.value),
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Time Zone
  const timeZone = useMemo(
    () =>
      wizardData?.timeZone?.map((item: IngestionKeyValueItem) => ({
        key: item.value,
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // Date Format
  const dateFormat = useMemo(
    () =>
      wizardData?.dateFormat?.map((item: IngestionKeyValueItem) => ({
        key: item.value,
        displayValue: item.text,
      })) ?? [],
    [wizardData]
  );

  // ✅ NEW: Link Type options for "Other" source
  const linkType = useMemo(
    () => [
      { key: 'TRANSLATION', displayValue: 'Translation' },
      { key: 'RELATED', displayValue: 'Related' },
    ],
    []
  );

  // Fetch folders
  const {data: rawSourceFolders} = useGetIngestionSourceFolders(
    selectedServerLocation,
    rawLstServerLocation
  );

  const sourceFolderOptions = useMemo(
    () =>
      rawSourceFolders?.map((item: IngestionKeyValueItem) => ({
        key: item.key,
        displayValue: item.value,
      })) ?? [],
    [rawSourceFolders]
  );

  // Fetch folder data
  const {data: rawSourceFolderData} = useGetIngestionSourceFoldersData(
    selectedServerLocation,
    rawLstServerLocation,
    selectedFolder
  );

  // ✅ UPDATED: Sort Load Files alphabetically by displayValue
  const loadFiles = useMemo(() => {
    const files =
      rawSourceFolderData?.listLoadFiles?.map(
        (item: IngestionKeyValueItem) => ({
          key: item.value,
          displayValue: item.text ?? item.value ?? '',
        })
      ) ?? [];

    // ✅ Sort alphabetically by displayValue
    return files.sort((a, b) =>
      a.displayValue.localeCompare(b.displayValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }, [rawSourceFolderData]);

  const datFileValue = rawSourceFolderData?.loadDAT ?? null;

  // Fetch headers
  const {data: sourceHeadersData} = useGetIngestionSourceHeaders(
    selectedServerLocation,
    rawLstServerLocation,
    selectedFolder,
    fieldSeparatorValue?.key ?? null,
    textQualifierValue?.key ?? null,
    dataDelimiterValue?.key ?? null,
    datLoadFile?.key ?? datFileValue ?? null
  );

  // ✅ UPDATED: Sort Headers alphabetically by displayValue
  const headersDocData = useMemo(() => {
    const headers =
      sourceHeadersData?.list?.map((item: IngestionKeyValueItem) => ({
        key: item.value,
        displayValue: item.text ?? item.value ?? '',
      })) ?? [];

    // ✅ Sort alphabetically by displayValue
    return headers.sort((a, b) =>
      a.displayValue.localeCompare(b.displayValue, undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
  }, [sourceHeadersData]);

  return {
    wizardData,
    isLoading,
    lstServerLocation,
    rawLstServerLocation,
    sourceSystem,
    serverLocation,
    columnDelimeter,
    dataDelimeter,
    textQualifier,
    timeZone,
    dateFormat,
    linkType,
    sourceFolderOptions,
    rawSourceFolders,
    loadFiles,
    datFileValue,
    sourceHeadersData,
    headersDocData,
    ingestionTypeOptions
  };
};
